import { Caption1, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { CheckmarkFilled } from '@fluentui/react-icons';

import { CHANGE_STATUS, LIFECYCLE_STEPS, TChangeStatus, TLifecycleStep } from '../../models';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 0,
    overflowX: 'auto',
    paddingBottom: tokens.spacingVerticalXS,
  },
  step: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXXS,
    minWidth: '84px',
    flex: '1 0 auto',
  },
  markerRow: { display: 'flex', alignItems: 'center', width: '100%' },
  connector: { height: '2px', flex: 1, backgroundColor: tokens.colorNeutralStroke2 },
  connectorDone: { backgroundColor: tokens.colorBrandStroke1 },
  connectorHidden: { visibility: 'hidden' },
  marker: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '26px',
    height: '26px',
    borderRadius: tokens.borderRadiusCircular,
    border: `2px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
    fontWeight: tokens.fontWeightSemibold,
    flexShrink: 0,
  },
  markerDone: {
    border: `2px solid ${tokens.colorBrandStroke1}`,
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  markerActive: {
    border: `2px solid ${tokens.colorBrandStroke1}`,
    color: tokens.colorBrandForeground1,
  },
  markerCancelled: {
    border: `2px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground4,
  },
  label: { textAlign: 'center', color: tokens.colorNeutralForeground3 },
  labelActive: { color: tokens.colorNeutralForeground1, fontWeight: tokens.fontWeightSemibold },
});

type TProps = {
  currentStep: TLifecycleStep;
  status: TChangeStatus;
};

export const LifecycleStepper = ({ currentStep, status }: TProps) => {
  const classes = useStyles();
  const currentIndex = LIFECYCLE_STEPS.indexOf(currentStep);
  const cancelled = status === CHANGE_STATUS.CANCELLED;

  return (
    <div className={classes.root} role="list" aria-label="Change lifecycle">
      {LIFECYCLE_STEPS.map((step, index) => {
        const isDone = !cancelled && index < currentIndex;
        const isActive = !cancelled && index === currentIndex;
        return (
          <div key={step} className={classes.step} role="listitem" aria-current={isActive ? 'step' : undefined}>
            <div className={classes.markerRow}>
              <div
                className={mergeClasses(
                  classes.connector,
                  isDone || isActive ? classes.connectorDone : undefined,
                  index === 0 && classes.connectorHidden
                )}
              />
              <div
                className={mergeClasses(
                  classes.marker,
                  isDone && classes.markerDone,
                  isActive && classes.markerActive,
                  cancelled && classes.markerCancelled
                )}
              >
                {isDone ? <CheckmarkFilled /> : index + 1}
              </div>
              <div
                className={mergeClasses(
                  classes.connector,
                  isDone ? classes.connectorDone : undefined,
                  index === LIFECYCLE_STEPS.length - 1 && classes.connectorHidden
                )}
              />
            </div>
            <Caption1 className={mergeClasses(classes.label, isActive && classes.labelActive)}>{step}</Caption1>
          </div>
        );
      })}
    </div>
  );
};
